import React from 'react';

// Empirically, 24 W's fit in one line, and there are 5 lines available to us.
const MAX_CHARACTERS = 120;
// The number of W's whose width corresponds to the '...More' text
const NUM_CHARACTERS_TO_TRUNCATE = 4;

/* This regex was adapted from https://stackoverflow.com/a/3809435. Changes I made:
- Removed extraneous www group
- Made several subgroups non-capturing (?:blahblah)
- Made the overall regex a capturing group
When using String.prototype.split, captured groups are included in the output. The change here ensure
that the overall url is captured in the split but none of it's constituent parts.
 */
const urlRegex =
  /((?:http(?:s)?:\/\/.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b[-a-zA-Z0-9@:%_+.~#?&//=]*)/;

function isLink(word: string): boolean {
  return urlRegex.test(word);
}

function sanitizeURL(url: string): string {
  if (!url.startsWith('http')) {
    return `http://${url}`;
  }
  return url;
}

interface Part {
  text: string;
  url: string | undefined;
}

function getParts(descriptionString: string): Array<Part> {
  const stringParts = descriptionString.split(urlRegex);
  return stringParts.map((stringPart) => ({
    text: stringPart,
    url: isLink(stringPart) ? sanitizeURL(stringPart) : undefined,
  }));
}

function truncateIfTooLong(parts: Array<Part>, capacity: number): {parts: Array<Part>; truncated: boolean} {
  const selectedParts = [];
  let currentLength = 0;
  /* Keep selecting parts till we exceed capacity */
  while (parts.length > 0 && currentLength < capacity) {
    selectedParts.push(parts[0]);
    currentLength += parts[0].text.length;
    parts = parts.slice(1);
  }
  const truncated = currentLength > capacity; /* Was capacity exceeded */
  if (truncated) {
    /* We will have to truncate the last part */
    const lastPart = selectedParts.pop();
    if (lastPart) {
      lastPart.text = lastPart.text.slice(0, lastPart.text.length - (currentLength - capacity));
      selectedParts.push(lastPart);
    }
  }
  return {parts: selectedParts, truncated};
}

function WorkflowDescription(props: WorkflowDescriptionProps): JSX.Element {
  const descriptionString = props.description || '';
  const {parts, truncated} = truncateIfTooLong(
    getParts(descriptionString),
    MAX_CHARACTERS - NUM_CHARACTERS_TO_TRUNCATE
  );
  return (
    <div className="u-word-break-word">
      {parts.map(({text, url}, idx) => (
        <LinkOrText key={idx} text={text} url={url} />
      ))}
      {truncated ? ['...', <a onClick={props.onClickMore}>More</a>] : null}
    </div>
  );
}

interface WorkflowDescriptionProps {
  description: string | null;
  onClickMore: () => void;
}

export default WorkflowDescription;

function LinkOrText(props: Part): JSX.Element {
  if (props.url) {
    return <a href={props.url}>{props.text}</a>;
  }
  return <span>{props.text}</span>;
}
